/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useState, useMemo, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { SecurityHelper } from 'sarsaparilla';

import { COMMERCIAL_TOURS } from 'ui-ticket/src/constants/index.js';
import { shouldMakeCommercialValidation } from 'ui-ticket/src/utils/tourUtils';
import { getToursByGatewayUrl, getTourImagesUrl } from '../../api/endpoints';
import {
    getToursByGatewayUrl as getTicketToursByGatewayUrl,
    getTourImagesUrl as getTicketTourImagesUrl,
} from '../../api/endpoints-tickets';
import { getAccountTypeSelector, processMediaApiResponse } from '../../utils';
import {
    COMMERCIAL_ACCOUNT_TYPE,
    INVENTORY_TICKET,
    INVENTORY_TIMED_ENTRY,
} from '../../constants';

const useGatewayTours = (tourId, recAreaData) => {
    const account = useSelector((state) => state?.auth?.account?.account);
    const [toursData, setToursData] = useState({
        tours: [],
        tourImageMap: {},
        status: 'IDLE',
    });

    // TODO: this hook needs a complete refactor when backend finds a solution to it

    const checkTicket = (tour, accountType) => {
        if (
            tour?.inventoryType === INVENTORY_TICKET &&
            shouldMakeCommercialValidation(tour?.facility_id)
        ) {
            // do not show non-com tours to com users
            if (
                accountType === COMMERCIAL_ACCOUNT_TYPE &&
                !COMMERCIAL_TOURS.includes(tour.tour_id)
            ) {
                return false;
            }

            // do not show comm tours to non-comm users
            if (
                accountType !== COMMERCIAL_ACCOUNT_TYPE &&
                COMMERCIAL_TOURS.includes(tour.tour_id)
            ) {
                return false;
            }
        }

        return true;
    };

    const checkTimedEntry = (tour, accountType) => {
        if (tour?.inventoryType === INVENTORY_TIMED_ENTRY) {
            // do not show comm tours to non-comm users
            if (accountType !== COMMERCIAL_ACCOUNT_TYPE && tour?.is_commercial_quota) {
                return false;
            }

            // do not show non-com tours to com users
            if (accountType === COMMERCIAL_ACCOUNT_TYPE && !tour?.is_commercial_quota) {
                return false;
            }
        }

        return true;
    };

    const filteredTours = useMemo(() => {
        return toursData?.tours?.filter((tour) => {
            const accountType = getAccountTypeSelector(account, tour?.facility_id);

            if (!tourId) return false;

            if (tour?.tour_id === tourId) return false;

            return checkTicket(tour, accountType) && checkTimedEntry(tour, accountType);
        });
    }, [account, tourId, toursData?.tours]);

    const filteredToursWithCustomData = useMemo(() => {
        return filteredTours?.map((tour) => {
            const {
                tour_id: localTourId,
                tour_name: customTourName,
                facility_id: facilityId,
                inventoryType,
            } = tour || {};
            const isTourTicket = inventoryType === INVENTORY_TICKET;
            const city = recAreaData?.city || '';
            const stateCode = recAreaData?.state_code || '';
            const locationTexts = [city, stateCode];
            const customLocation = locationTexts?.filter((text) => !!text)?.join(', ');
            const customLinkUrl = `/${
                isTourTicket ? 'ticket' : 'timed-entry'
            }/${facilityId}/ticket/${localTourId}`;
            const tourKey = isTourTicket ? localTourId : `${facilityId}_${localTourId}`;
            const customImageUrl =
                toursData?.tourImageMap?.[tourKey]?.previewImage?.url ||
                toursData?.tourImageMap?.[tourKey]?.heroImage?.url ||
                toursData?.tourImageMap?.[tourKey]?.galleryImages?.[0]?.url;

            return {
                ...tour,
                customImageUrl,
                customTourName,
                customLinkUrl,
                customLocation,
            };
        });
    }, [
        filteredTours,
        recAreaData?.city,
        recAreaData?.state_code,
        toursData?.tourImageMap,
    ]);

    const getTourImage = (facilityId, localTourId, inventoryType) => {
        let url = getTourImagesUrl(facilityId, localTourId);

        if (inventoryType === INVENTORY_TICKET) {
            url = getTicketTourImagesUrl(localTourId);
        }

        return axios.get(url);
    };

    const getToursByGateway = useCallback(async () => {
        try {
            if (recAreaData?.entity_id && toursData?.status !== 'LOADING') {
                setToursData((prevState) => ({
                    ...prevState,
                    tours: [],
                    status: 'LOADING',
                }));

                const timedEntryurl = getToursByGatewayUrl(recAreaData?.entity_id);
                const ticketUrl = getTicketToursByGatewayUrl(recAreaData?.entity_id);
                let promises = [];
                const timedEntryPromise = axios.get(timedEntryurl, {
                    headers: {
                        Authorization: SecurityHelper.getAuthHeader(),
                    },
                });

                const ticketPromise = axios.get(ticketUrl, {
                    headers: {
                        Authorization: SecurityHelper.getAuthHeader(),
                    },
                });

                promises = [timedEntryPromise, ticketPromise];
                const [{ data: timedEntrytours = [] }, { data: ticketTours = [] }] =
                    await Promise.all(promises);

                const timedEntrytoursWithType = timedEntrytours?.map((tour) => ({
                    ...tour,
                    inventoryType: INVENTORY_TIMED_ENTRY,
                }));
                const ticketToursWithType = ticketTours?.map((tour) => ({
                    ...tour,
                    inventoryType: INVENTORY_TICKET,
                }));

                const tours = [...timedEntrytoursWithType, ...ticketToursWithType];

                setToursData((prevState) => ({
                    ...prevState,
                    tours,
                    status: 'IDLE',
                }));
            }
        } catch (error) {
            setToursData((prevState) => ({
                ...prevState,
                status: 'ERROR',
            }));
        }
    }, [recAreaData.entity_id, toursData.status]);

    const getTourImages = useCallback(async () => {
        try {
            const promises = [];

            filteredTours.forEach((tour) => {
                const { facility_id: facilityId, tour_id: localTourId } = tour;
                promises.push(getTourImage(facilityId, localTourId, tour?.inventoryType));
            });

            const data = await Promise?.allSettled(promises);
            const responses = [];
            data.forEach((item) => {
                if (item?.status === 'fulfilled') {
                    responses.push(item?.value?.data?.result);
                }
            });
            const tourImageMap = {};
            responses.forEach((item) => {
                const localTourId = item?.[0]?.entity_id;

                if (localTourId) {
                    tourImageMap[localTourId] = processMediaApiResponse(item);
                }
            });
            setToursData((prevState) => ({ ...prevState, tourImageMap }));
        } catch (error) {
            // console.log(
            //     '🚀 ~ file: useGatewayTours.js ~ line 164 ~ getTourImages ~ error',
            //     error
            // );
        }
    }, [filteredTours]);

    useEffect(() => {
        getToursByGateway();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recAreaData.entity_id]);

    useEffect(() => {
        getTourImages();
    }, [getTourImages]);

    return { tours: filteredToursWithCustomData, status: toursData?.status };
};

export default useGatewayTours;
